/**
 * Keeping a newly added item in sight.
 *
 * An item that lands behind a panel, off the edge of the plan or outside the camera
 * reads to the user as "nothing happened". Placement therefore aims at the part of the
 * stage that is actually free, and the view follows the item when a room constraint or
 * an opening panel still leaves it out of sight.
 */
export interface ScreenRect { x: number; y: number; width: number; height: number }
export interface ScreenPoint { x: number; y: number }

/** How far inside the free area a new item is aimed, so it never lands against an edge. */
export const SIGHT_INSET = 48;

/**
 * How far inside the free area an item still counts as seen. Deliberately smaller than the
 * aim: taking the view off the user is worth it for an item they cannot see at all, not for
 * one sitting a little closer to a panel than the aim would have chosen.
 */
export const REVEAL_INSET = 8;

/** Below this the remaining strip is too small to place anything in, so the panel is ignored. */
const MIN_SIDE = 96;

/**
 * The part of `stage` no editor chrome covers. Every overlapping panel takes one side with
 * it — a right-hand inspector the right, a catalog drawer the bottom — and the side chosen
 * is the one that leaves the most room, which is what keeps a tall left rail from eating
 * the top of the stage. A panel with no cut worth making is ignored rather than collapsing
 * the area to a sliver.
 */
export function visibleStageArea(stage: ScreenRect, obstacles: Iterable<ScreenRect>, margin = 12): ScreenRect {
  let left = stage.x, top = stage.y, right = stage.x + stage.width, bottom = stage.y + stage.height;
  for (const obstacle of obstacles) {
    if (!(obstacle.width > 0) || !(obstacle.height > 0)) continue;
    const oLeft = obstacle.x, oTop = obstacle.y, oRight = oLeft + obstacle.width, oBottom = oTop + obstacle.height;
    if (oRight <= left || oLeft >= right || oBottom <= top || oTop >= bottom) continue;
    const options = [
      { left: oRight + margin, top, right, bottom },
      { left, top, right: oLeft - margin, bottom },
      { left, top: oBottom + margin, right, bottom },
      { left, top, right, bottom: oTop - margin },
    ].filter(cut => cut.right - cut.left >= MIN_SIDE && cut.bottom - cut.top >= MIN_SIDE);
    if (!options.length) continue;
    const best = options.reduce((widest, cut) =>
      (cut.right - cut.left) * (cut.bottom - cut.top) > (widest.right - widest.left) * (widest.bottom - widest.top) ? cut : widest);
    ({ left, top, right, bottom } = best);
  }
  return { x: left, y: top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/** Where a placement with no pointer of its own belongs: the middle of what is visible. */
export function areaCentre(area: ScreenRect): ScreenPoint {
  return { x: area.x + area.width / 2, y: area.y + area.height / 2 };
}

/** Whether a projected point is inside the free area, `inset` allowing for the item's own size. */
export function withinArea(point: ScreenPoint | null | undefined, area: ScreenRect, inset = 0): boolean {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
  const slack = Math.min(inset, area.width / 2, area.height / 2);
  return point.x >= area.x + slack && point.x <= area.x + area.width - slack
    && point.y >= area.y + slack && point.y <= area.y + area.height - slack;
}
